import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

// Three callers, three auth models:
// - author: workpay author, session-authed, subscribes by target
// - reviewer: external reviewer (email link), token-authed, subscribes by token
// - reviewer-internal: session-authed INTERNAL reviewer (tenant user or workpay
//   colleague) for an in-portal review, subscribes by reviewId
type UseReviewSseOptions =
  | {
      mode: 'author';
      targetType: 'TEMPLATE' | 'ENVELOPE';
      targetId: string;
      enabled?: boolean;
      baseUrl?: string;
      /**
       * The selected company, put on the QUERY STRING because `EventSource`
       * cannot send a header.
       *
       * The scope normally travels in `X-Active-Tenant`, so every review stream
       * reached the gateway naming no company, was read as all-companies, and
       * was refused with `48034 K_TENANT_SELECTION_REQUIRED` before any handler
       * ran. The pages still updated — all four also poll — so it showed up only
       * as a failed request every 30s per open tab. Handover §29.
       *
       * `null` when no company is selected, and then the parameter is OMITTED
       * rather than sent blank: absent and empty are different states and the
       * caller's store already distinguishes them.
       *
       * Not trusted by the server. The gateway validates it against the session
       * exactly as it validates the header, refuses a company the session does
       * not hold with `48032`, and re-stamps it as a real `X-Tenant-Id` before
       * any service sees it.
       *
       * Supplied by the CALLER rather than read from a store, because this hook
       * lives in ds-shared and must not reach into an app's state.
       */
      activeTenantId?: string | null;
      /**
       * The API persona (`w` / `t` / `o` / `e`), inserted after `/v1`:
       * `/v1/<persona>/reviews/stream`. Supplied by the caller, since this package
       * must not reach into app state. Absent → the un-prefixed path.
       */
      persona?: string;
    }
  | {
      mode: 'reviewer';
      token: string;
      targetType?: 'TEMPLATE' | 'ENVELOPE';
      targetId?: string;
      enabled?: boolean;
      baseUrl?: string;
    }
  | {
      mode: 'reviewer-internal';
      reviewId: string;
      snapshotId: string;
      enabled?: boolean;
      baseUrl?: string;
      /** See the `author` variant. Same reason, same rules. */
      activeTenantId?: string | null;
      // No `persona`: `reviews/{id}/reviewer-stream` is a shared endpoint (one URL for
      // every persona), unlike the author stream.
    };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_TARGET_TYPES = new Set(['TEMPLATE', 'ENVELOPE']);
// Safety net for a rejection the server does NOT announce with `stream_closed` —
// BUG-7106 looped forever on exactly that. ~10 min of retries (5s + 15s + 18×30s).
const MAX_RETRIES = 20;

// ── Event handling ────────────────────────────────────────────────────
// Invalidations per mode. Pulled out so both onmessage and onopen (reconnect)
// can trigger the same refresh — the SSE stream has NO replay on reconnect,
// so refetching the authoritative REST endpoints covers missed events.

function invalidateForMode(
  opts: UseReviewSseOptions,
  queryClient: QueryClient,
  onStatusChange: boolean,
) {
  if (opts.mode === 'author') {
    void queryClient.invalidateQueries({
      queryKey: ['ds-reviews', opts.targetType, opts.targetId],
    });
    if (onStatusChange) {
      if (opts.targetType === 'TEMPLATE') {
        void queryClient.invalidateQueries({
          queryKey: ['dsTemplate', opts.targetId],
        });
      } else {
        void queryClient.invalidateQueries({
          queryKey: ['dsEnvelope', opts.targetId],
        });
      }
    }
  } else if (opts.mode === 'reviewer') {
    if (opts.targetType && opts.targetId) {
      void queryClient.invalidateQueries({
        queryKey: ['ds-reviews', opts.targetType, opts.targetId],
      });
    }
  } else if (opts.mode === 'reviewer-internal') {
    void queryClient.invalidateQueries({
      queryKey: ['dsSnapshot-my-review', opts.snapshotId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['dsSnapshot-reviewer-activities', opts.reviewId],
    });
    if (onStatusChange) {
      // Snapshot row status may have changed (IN_REVIEW → DRAFT after dual-completion).
      // Invalidate both tenant + workpay cross-org keys; only one is cached.
      void queryClient.invalidateQueries({
        queryKey: ['dsSnapshot', opts.snapshotId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['dsCrossOrgSnapshot', opts.snapshotId],
      });
    }
  }
}

/**
 * `&activeTenant=<id>`, or nothing at all.
 *
 * Nothing is the point: a blank `activeTenant=` is a third spelling of a state
 * that already has two, and the gateway would have to guess which was meant.
 */
function tenantParam(activeTenantId: string | null | undefined, separator: '?' | '&'): string {
  return activeTenantId ? `${separator}activeTenant=${encodeURIComponent(activeTenantId)}` : '';
}

function buildUrl(opts: UseReviewSseOptions, baseUrl: string): string | null {
  if (opts.mode === 'author' && opts.targetType && opts.targetId) {
    return (
      `${baseUrl}/v1/${opts.persona ? `${opts.persona}/` : ''}reviews/stream?targetType=${encodeURIComponent(opts.targetType)}&targetId=${encodeURIComponent(opts.targetId)}` +
      tenantParam(opts.activeTenantId, '&')
    );
  }
  if (opts.mode === 'reviewer' && opts.token) {
    // DELIBERATELY BARE. This is the public, token-authed external stream: it
    // skips the company gate entirely and ignores the parameter. The option does
    // not exist on this variant, so the omission is enforced by the compiler
    // rather than by remembering.
    return `${baseUrl}/v1/review-session/${encodeURIComponent(opts.token)}/stream`;
  }
  if (opts.mode === 'reviewer-internal' && opts.reviewId) {
    return (
      `${baseUrl}/v1/reviews/${encodeURIComponent(opts.reviewId)}/reviewer-stream` +
      tenantParam(opts.activeTenantId, '?')
    );
  }
  return null;
}

function validateIds(opts: UseReviewSseOptions): boolean {
  if (opts.mode === 'author') {
    if (!UUID_RE.test(opts.targetId)) return false;
    if (!SAFE_TARGET_TYPES.has(opts.targetType)) return false;
  } else if (opts.mode === 'reviewer') {
    if (opts.targetId && !UUID_RE.test(opts.targetId)) return false;
    if (opts.targetType && !SAFE_TARGET_TYPES.has(opts.targetType)) return false;
  } else if (opts.mode === 'reviewer-internal') {
    if (!UUID_RE.test(opts.reviewId)) return false;
    if (!UUID_RE.test(opts.snapshotId)) return false;
  }
  return true;
}

export function useReviewSse(opts: UseReviewSseOptions) {
  const { enabled = true, baseUrl = '/api/contract-service' } = opts;
  const queryClient = useQueryClient();
  const retryCountRef = useRef(0);
  // Prevent double-fetch on initial connection — the consumer queries
  // are already in flight from mount. Only invalidate on true reconnects.
  const isFirstOpenRef = useRef(true);
  // The server answered `stream_closed`: dead/expired token, or a caller no longer
  // allowed to see the target. Terminal — reconnecting only gets the same answer.
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!validateIds(opts)) return;

    const url = buildUrl(opts, baseUrl);
    if (!url) return;

    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      // Session-authed modes (author / reviewer-internal) need the cookie to
      // travel cross-origin — deployed consumers point baseUrl at
      // `https://api.<apex>`. The token-authed `reviewer` mode (ds-external) is
      // cookieless, so it stays withCredentials:false (avoids a CORS-credentials
      // requirement on the public review-session stream).
      const withCredentials = opts.mode !== 'reviewer';
      es = new EventSource(url!, { withCredentials });

      // addEventListener, NOT onmessage: the server names every event, and
      // onmessage only receives UNNAMED ones.
      es.addEventListener('stream_closed', () => {
        stoppedRef.current = true;
        if (retryTimer) clearTimeout(retryTimer);
        es?.close();
      });

      es.onopen = () => {
        retryCountRef.current = 0;
        if (isFirstOpenRef.current) {
          isFirstOpenRef.current = false;
          return;
        }
        // Reconnect: refetch authoritative state. SSE has no replay —
        // any events dropped during the disconnect window are gone from
        // the stream. The REST endpoints always return the full current state.
        invalidateForMode(opts, queryClient, true);
      };

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as Record<string, unknown>;
          const eventType = data.type;

          if (eventType === 'activity_added' || eventType === 'reviewer_status_changed') {
            invalidateForMode(opts, queryClient, false);
          }
          if (eventType === 'review_status_changed') {
            invalidateForMode(opts, queryClient, true);
          }
        } catch {
          /* ignore parse errors */
        }
      };

      es.onerror = () => {
        es?.close();
        // The server closes the stream right after `stream_closed`, which fires
        // onerror. Without this the hook reconnects anyway and nothing changes.
        if (stoppedRef.current) return;
        if (retryCountRef.current >= MAX_RETRIES) return;
        const delays = [5000, 15000, 30000];
        const delay = delays[Math.min(retryCountRef.current, delays.length - 1)]!;
        retryCountRef.current++;
        retryTimer = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      es?.close();
      if (retryTimer) clearTimeout(retryTimer);
      // Reset the first-open guard when the effect tears down so a remount
      // (e.g. changing reviewId) starts a fresh session cleanly.
      isFirstOpenRef.current = true;
      // Otherwise the next review opened in this app session never connects.
      stoppedRef.current = false;
      // Refs outlive the effect: without this a re-run inherits a climbed counter
      // and waits 30s before its first retry instead of 5s.
      retryCountRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opts.mode,
    // Stable deps per mode — extract only primitive fields that drive URL identity
    'targetType' in opts ? opts.targetType : undefined,
    'targetId' in opts ? opts.targetId : undefined,
    'token' in opts ? opts.token : undefined,
    'reviewId' in opts ? opts.reviewId : undefined,
    'snapshotId' in opts ? opts.snapshotId : undefined,
    // Rule 3 (§29): the company is fixed at handshake, so switching company must
    // tear the connection down and reopen it — otherwise a tab keeps streaming
    // the company it was opened with.
    'activeTenantId' in opts ? opts.activeTenantId : undefined,
    // Same rule: the persona segment is part of the URL.
    'persona' in opts ? opts.persona : undefined,
    enabled,
    baseUrl,
    queryClient,
  ]);
}
